/**
 * ProjectDescription Component
 *
 * Renders the project's overall summary beneath the title/owner on the project
 * details page, so the goal doesn't have to be re-explained in every task.
 *
 * Long descriptions are clamped with a Show more/less toggle, matching the
 * collapsible pattern used by TaskDescriptionCard. With no description set, it
 * points at Settings instead, so the field is discoverable.
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import '../../styles/index.scss';

const ProjectDescription = ({ description, isMobile, onAddDescription }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const contentRef = useRef(null);

  // Only offer the toggle when the text actually overflows the clamp. Measuring
  // against clientHeight keeps this in step with the CSS instead of a duplicated
  // pixel constant; skipped while expanded, where the two heights always match.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || isExpanded) return;
    setNeedsExpansion(el.scrollHeight > el.clientHeight + 1);
  }, [description, isMobile, isExpanded]);

  const wrapperClass = `project-description ${isMobile ? 'project-description--mobile' : ''}`;

  // Point at the field rather than rendering nothing: projects predating this view
  // have an empty description, and the summary lives behind the Settings modal.
  if (!description || !description.trim()) {
    return (
      <div className={wrapperClass}>
        <Text type="secondary" className="project-description__empty">
          No description yet.
        </Text>
        {onAddDescription && (
          <Button
            type="link"
            className="project-description__add-btn"
            onClick={onAddDescription}
          >
            Add one in Settings
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div
        ref={contentRef}
        className={`project-description__text ${
          isExpanded ? 'project-description__text--expanded' : ''
        }`}
      >
        {description}
      </div>

      {needsExpansion && (
        <Button
          type="link"
          className="project-description__toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <>Show less <UpOutlined /></>
          ) : (
            <>Show more <DownOutlined /></>
          )}
        </Button>
      )}
    </div>
  );
};

ProjectDescription.propTypes = {
  description: PropTypes.string,
  isMobile: PropTypes.bool,
  onAddDescription: PropTypes.func,
};

ProjectDescription.defaultProps = {
  description: '',
  isMobile: false,
  onAddDescription: null,
};

export default ProjectDescription;
